import { readGauge, type ReadDeadlinePhase } from './read-gauge'

/** Per fetch attempt, not a whole-query budget: transient failures may still retry. */
export const SUPABASE_FETCH_DEADLINE_MS = 8000

/** Safari versions supporting timeout() may still lack any(). */
function combineSignals(caller: AbortSignal, deadline: AbortSignal): AbortSignal {
  if (typeof AbortSignal.any === 'function') return AbortSignal.any([caller, deadline])
  const controller = new AbortController()
  const cleanup = () => {
    caller.removeEventListener('abort', onCallerAbort)
    deadline.removeEventListener('abort', onDeadlineAbort)
  }
  const abort = (reason: unknown) => {
    cleanup()
    controller.abort(reason)
  }
  const onCallerAbort = () => abort(caller.reason)
  const onDeadlineAbort = () => abort(deadline.reason)
  if (caller.aborted) onCallerAbort()
  else if (deadline.aborted) onDeadlineAbort()
  else {
    caller.addEventListener('abort', onCallerAbort, { once: true })
    deadline.addEventListener('abort', onDeadlineAbort, { once: true })
  }
  return controller.signal
}

/** Shared by every copy of this module in the process (lib/supabase/read-gauge.ts). */
const gauge = readGauge()

function requestUrl(input: Parameters<typeof fetch>[0]): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return input.url
}

/** FRIDGE-1615: tell the stall reporter, when one is installed, that this read hit its deadline. */
function reportDeadline(phase: ReadDeadlinePhase, input: Parameters<typeof fetch>[0]): void {
  const hook = gauge.onDeadline
  if (!hook) return
  try {
    hook(phase, requestUrl(input))
  } catch {
    // The signal is diagnostics; it never changes the read it reports on.
  }
}

/**
 * FRIDGE-1615: the deadline also covers the body, and in production that is where it mostly fires. Of the 7 page
 * 504s from 10/19/2026 to 10/20/2026 whose render first logged a read deadline, 6 read "TimeoutError: The operation
 * was aborted due to timeout", which the PostgREST client gets from `res.text()`, and 1 read "Supabase fetch deadline
 * exceeded". The client reads every body with `text()`, so this response's own `text` counts the body
 * read in flight and reports a deadline that ends it. Other body readers (`json()`, a stream) behave as before and
 * are simply not counted. One closure per read; nothing else until `text()` is called.
 */
function watchBody(response: Response, deadline: AbortSignal, input: Parameters<typeof fetch>[0]): void {
  const read = response.text
  try {
    response.text = () => {
      gauge.readingBody++
      return read.call(response).then(
        (body) => {
          gauge.readingBody--
          return body
        },
        (error: unknown) => {
          // Reported while still counted, as a read that dies before headers is.
          if (deadline.aborted) reportDeadline('body', input)
          gauge.readingBody--
          throw error
        },
      )
    }
  } catch {
    // A response that cannot take an own property is returned untouched.
  }
}

/**
 * Composed signals retain cancellation through response-body consumption.
 * Keep cancellation past headers; fallback listeners are removed on cancellation
 * or the deadline, including when the response finishes before that deadline.
 * PostgREST retries TimeoutError, but recognizes AbortError as terminal cancellation.
 */
export const fetchWithSupabaseDeadline: typeof fetch = async (input, init) => {
  const requestSignal = typeof Request !== 'undefined' && input instanceof Request ? input.signal : undefined
  // Fetch semantics: undefined inherits Request.signal; null explicitly clears it.
  const callerSignal = init?.signal === undefined ? requestSignal : init.signal
  if (callerSignal?.aborted) throw new DOMException('Supabase request cancelled', 'AbortError')
  const deadline = AbortSignal.timeout(SUPABASE_FETCH_DEADLINE_MS)
  const signal = callerSignal ? combineSignals(callerSignal, deadline) : deadline
  let response: Response
  gauge.awaitingResponse++
  try {
    response = await fetch(input, { ...init, signal })
  } catch (error) {
    if (signal.aborted) {
      if (deadline.aborted) reportDeadline('headers', input)
      throw new DOMException(deadline.aborted ? 'Supabase fetch deadline exceeded' : 'Supabase request cancelled', 'AbortError')
    }
    throw error
  } finally {
    gauge.awaitingResponse--
  }
  watchBody(response, deadline, input)
  return response
}
